import React, { useState, useEffect, useCallback } from 'react';
import { gadsScriptAPI } from '../utils/api';

const SYNC_OPTIONS = [
  { value: 'EVERY_HOUR', label: 'Every hour' },
  { value: 'EVERY_2_HOURS', label: 'Every 2 hours' },
  { value: 'EVERY_4_HOURS', label: 'Every 4 hours' },
  { value: 'EVERY_6_HOURS', label: 'Every 6 hours' },
  { value: 'EVERY_12_HOURS', label: 'Every 12 hours' },
  { value: 'EVERY_DAY', label: 'Once a day' },
];

function generateScript(apiKey, apiUrl, syncInterval, autoExclude) {
  return `// ClickSaver — Google Ads Script
// Syncs campaigns to ClickSaver and excludes blocked IPs automatically.
// Schedule: ${SYNC_OPTIONS.find(o => o.value === syncInterval)?.label || 'Every hour'}

var CONFIG = {
  API_URL: '${apiUrl}',
  API_KEY: '${apiKey}',
  AUTO_EXCLUDE: ${autoExclude},  // Push blocked IPs as campaign IP exclusions
  MAX_EXCLUSIONS: 500,          // Google Ads limit per campaign
};

function main() {
  Logger.log('ClickSaver sync started...');

  // Step 1: Push all campaigns to ClickSaver
  var campaigns = pushCampaigns_();

  // Step 2: Pull blocked IPs and add as exclusions
  if (CONFIG.AUTO_EXCLUDE) {
    excludeBlockedIPs_(campaigns);
  }

  Logger.log('ClickSaver sync complete.');
}

function pushCampaigns_() {
  var campaigns = [];
  var campaignIterator = AdsApp.campaigns()
    .withCondition('Status != REMOVED')
    .get();

  while (campaignIterator.hasNext()) {
    var campaign = campaignIterator.next();
    var stats = campaign.getStatsFor('LAST_30_DAYS');
    campaigns.push({
      id: campaign.getId().toString(),
      name: campaign.getName(),
      status: campaign.isEnabled() ? 'ENABLED' : campaign.isPaused() ? 'PAUSED' : 'OTHER',
      type: campaign.getAdvertisingChannelType ? campaign.getAdvertisingChannelType() : 'SEARCH',
      clicks: stats.getClicks(),
      impressions: stats.getImpressions(),
      cost: stats.getCost(),
      conversions: stats.getConversions(),
      cpc: stats.getClicks() > 0 ? (stats.getCost() / stats.getClicks()) : 0,
    });
  }

  // Also get Shopping campaigns
  try {
    var shoppingIterator = AdsApp.shoppingCampaigns()
      .withCondition('Status != REMOVED')
      .get();
    while (shoppingIterator.hasNext()) {
      var campaign = shoppingIterator.next();
      var stats = campaign.getStatsFor('LAST_30_DAYS');
      campaigns.push({
        id: campaign.getId().toString(),
        name: campaign.getName(),
        status: campaign.isEnabled() ? 'ENABLED' : 'PAUSED',
        type: 'SHOPPING',
        clicks: stats.getClicks(),
        impressions: stats.getImpressions(),
        cost: stats.getCost(),
        conversions: stats.getConversions(),
        cpc: stats.getClicks() > 0 ? (stats.getCost() / stats.getClicks()) : 0,
      });
    }
  } catch (e) { /* No shopping campaigns */ }

  // Also get Performance Max campaigns
  try {
    var pmaxIterator = AdsApp.performanceMaxCampaigns()
      .withCondition('Status != REMOVED')
      .get();
    while (pmaxIterator.hasNext()) {
      var campaign = pmaxIterator.next();
      var stats = campaign.getStatsFor('LAST_30_DAYS');
      campaigns.push({
        id: campaign.getId().toString(),
        name: campaign.getName(),
        status: campaign.isEnabled() ? 'ENABLED' : 'PAUSED',
        type: 'PERFORMANCE_MAX',
        clicks: stats.getClicks(),
        impressions: stats.getImpressions(),
        cost: stats.getCost(),
        conversions: stats.getConversions(),
        cpc: stats.getClicks() > 0 ? (stats.getCost() / stats.getClicks()) : 0,
      });
    }
  } catch (e) { /* No PMax campaigns */ }

  var response = UrlFetchApp.fetch(CONFIG.API_URL + '/api/gads-script/push-campaigns', {
    method: 'post',
    contentType: 'application/json',
    payload: JSON.stringify({ api_key: CONFIG.API_KEY, campaigns: campaigns }),
    muteHttpExceptions: true,
  });

  Logger.log('Pushed ' + campaigns.length + ' campaigns. Response: ' + response.getContentText());
  return campaigns;
}

function excludeBlockedIPs_(campaigns) {
  var campaignIds = campaigns.map(function(c) { return c.id; });

  var response = UrlFetchApp.fetch(CONFIG.API_URL + '/api/gads-script/get-blocked-ips', {
    method: 'post',
    contentType: 'application/json',
    payload: JSON.stringify({ api_key: CONFIG.API_KEY, campaign_ids: campaignIds }),
    muteHttpExceptions: true,
  });

  var data = JSON.parse(response.getContentText());
  var blockedIPs = data.ips || [];

  if (blockedIPs.length === 0) {
    Logger.log('No blocked IPs to exclude.');
    return;
  }

  Logger.log('Found ' + blockedIPs.length + ' blocked IPs to exclude.');

  // Apply exclusions to each campaign
  var campaignIterator = AdsApp.campaigns()
    .withCondition('Status != REMOVED')
    .get();

  while (campaignIterator.hasNext()) {
    var campaign = campaignIterator.next();
    var campaignName = campaign.getName();

    // Get existing exclusions
    var existingIPs = {};
    var excludedIterator = campaign.targeting().excludedIpAddresses().get();
    var existingCount = 0;
    while (excludedIterator.hasNext()) {
      var excluded = excludedIterator.next();
      existingIPs[excluded.getIpAddress()] = true;
      existingCount++;
    }

    // Add new exclusions (respect the 500 limit)
    var added = 0;
    for (var i = 0; i < blockedIPs.length; i++) {
      if (existingCount + added >= CONFIG.MAX_EXCLUSIONS) {
        Logger.log(campaignName + ': Hit 500 IP exclusion limit.');
        break;
      }
      if (!existingIPs[blockedIPs[i]]) {
        campaign.targeting().newIpAddressBuilder()
          .withIpAddress(blockedIPs[i])
          .exclude()
          .build();
        added++;
      }
    }

    if (added > 0) {
      Logger.log(campaignName + ': Added ' + added + ' IP exclusions (' + (existingCount + added) + '/500).');
    }
  }
}`;
}

export default function GoogleAdsScript() {
  const [settings, setSettings] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [syncInterval, setSyncInterval] = useState('EVERY_HOUR');
  const [autoExclude, setAutoExclude] = useState(true);

  const apiUrl = process.env.REACT_APP_API_URL || 'https://clicksaver.app';

  const fetchData = useCallback(async () => {
    try {
      const [s, c] = await Promise.all([
        gadsScriptAPI.getSettings(),
        gadsScriptAPI.getStoredCampaigns(),
      ]);
      setSettings(s);
      setCampaigns(c);
      setSyncInterval(s.sync_interval || 'EVERY_HOUR');
      setAutoExclude(s.auto_exclude !== false);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleGenerateKey = async () => {
    setGenerating(true);
    try {
      const r = await gadsScriptAPI.generateKey();
      setSettings(prev => ({ ...prev, api_key: r.api_key }));
    } catch (e) {
      alert('Failed to generate API key');
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await gadsScriptAPI.updateSettings({ sync_interval: syncInterval, auto_exclude: autoExclude });
      setSettings(prev => ({ ...prev, sync_interval: syncInterval, auto_exclude: autoExclude }));
    } catch (e) {
      alert('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = () => {
    const script = generateScript(settings?.api_key || '', apiUrl, syncInterval, autoExclude);
    navigator.clipboard.writeText(script);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  if (loading) return <div className="loading"><div className="spinner" /> Loading...</div>;

  const hasKey = !!settings?.api_key;
  const hasSynced = !!settings?.last_sync;
  const script = generateScript(settings?.api_key || 'YOUR_API_KEY', apiUrl, syncInterval, autoExclude);

  return (
    <div>
      <div className="page-header">
        <h1>Google Ads Integration</h1>
        <p>Connect your Google Ads campaigns using a simple script — no API token needed</p>
      </div>

      {/* How it works */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="section-title">How It Works</div>
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          {[
            { step: '1', title: 'Generate API Key', desc: 'Click the button below to create your unique API key' },
            { step: '2', title: 'Copy the Script', desc: 'Copy the generated Google Ads Script below' },
            { step: '3', title: 'Paste in Google Ads', desc: 'Go to Google Ads → Tools → Scripts → paste and save' },
            { step: '4', title: 'Schedule & Run', desc: 'Set the schedule frequency and click Run. Campaigns sync automatically' },
          ].map(s => (
            <div key={s.step} style={{ flex: '1 1 180px' }}>
              <div style={{
                width: 32, height: 32, borderRadius: '50%', background: 'var(--accent-blue)',
                color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, fontSize: 14, marginBottom: 8,
              }}>{s.step}</div>
              <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 13 }}>{s.title}</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{s.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Settings card */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 12 }}>

        {/* API Key + Settings */}
        <div className="card">
          <div className="section-title">Script Settings</div>

          {/* API Key */}
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>API Key</label>
            {hasKey ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <code style={{
                  flex: 1, padding: '10px 14px', background: 'var(--bg-secondary)', border: '1px solid var(--border)',
                  borderRadius: 8, fontSize: 12, fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', color: 'var(--accent-green)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>{settings.api_key}</code>
                <button className="btn btn-ghost" onClick={handleGenerateKey} disabled={generating}
                  style={{ fontSize: 11, flexShrink: 0 }}>
                  Regenerate
                </button>
              </div>
            ) : (
              <button className="btn btn-primary" onClick={handleGenerateKey} disabled={generating}>
                {generating ? 'Generating...' : 'Generate API Key'}
              </button>
            )}
          </div>

          {/* Sync Interval */}
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>
              Sync Frequency
            </label>
            <select value={syncInterval} onChange={e => setSyncInterval(e.target.value)} style={{ width: '100%' }}>
              {SYNC_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
              How often the script runs in Google Ads. Set the same frequency when scheduling the script.
            </div>
          </div>

          {/* Auto Exclude toggle */}
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={autoExclude}
                onChange={e => setAutoExclude(e.target.checked)}
                style={{ width: 18, height: 18 }}
              />
              <div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>Auto-exclude blocked IPs</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  Automatically add blocked IPs to Google Ads campaign exclusions
                </div>
              </div>
            </label>
          </div>

          <button className="btn btn-primary" onClick={handleSaveSettings} disabled={saving} style={{ width: '100%' }}>
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>

        {/* Sync Status */}
        <div className="card">
          <div className="section-title">Sync Status</div>

          <div style={{ marginBottom: 12 }}>
            {[
              { label: 'API Key', ok: hasKey, text: hasKey ? 'Generated' : 'Not generated' },
              { label: 'Script Synced', ok: hasSynced, text: hasSynced ? `Last: ${new Date(settings.last_sync).toLocaleString()}` : 'Never synced' },
              { label: 'Campaigns Found', ok: settings?.campaign_count > 0, text: `${settings?.campaign_count || 0} campaigns` },
              { label: 'Auto-exclude', ok: autoExclude, text: autoExclude ? 'Enabled' : 'Disabled' },
            ].map((row, i) => (
              <div key={i} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '12px 0', borderBottom: i < 3 ? '1px solid var(--border)' : 'none',
              }}>
                <span style={{ fontSize: 13, color: 'var(--text-primary)' }}>{row.label}</span>
                <span style={{
                  fontSize: 12, fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', fontWeight: 600,
                  color: row.ok ? 'var(--accent-green)' : 'var(--text-muted)',
                }}>
                  {row.ok ? '~' : '~'} {row.text}
                </span>
              </div>
            ))}
          </div>

          {/* Synced campaigns list */}
          {campaigns.length > 0 && (
            <div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
                Synced Google Ads Campaigns:
              </div>
              <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                {campaigns.map((c, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0',
                    borderBottom: '1px solid var(--border)',
                  }}>
                    <span style={{
                      width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                      background: c.status === 'ENABLED' ? '#22c55e' : '#9ca3af',
                    }} />
                    <span style={{ fontSize: 12, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.name}
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                      {(c.clicks || 0).toLocaleString()} clicks
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Script code */}
      {hasKey && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div className="section-title" style={{ marginBottom: 0 }}>Google Ads Script</div>
            <button className="btn btn-primary" onClick={handleCopy} style={{ fontSize: 12 }}>
              {copied ? 'Copied!' : 'Copy Script'}
            </button>
          </div>

          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12, lineHeight: 1.6 }}>
            Copy this script and paste it in <strong>Google Ads → Tools → Bulk Actions → Scripts</strong>.
            Set the schedule to <strong>{SYNC_OPTIONS.find(o => o.value === syncInterval)?.label}</strong> and click <strong>Run</strong>.
          </div>

          <pre style={{
            background: '#0d1117', color: '#e6edf3', padding: 20, borderRadius: 10,
            fontSize: 12, lineHeight: 1.6, overflow: 'auto', maxHeight: 400,
            fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', border: '1px solid #30363d',
          }}>
            {script}
          </pre>

          {/* Step by step instructions */}
          <div style={{ marginTop: 16, padding: '16px 20px', background: 'rgba(88,166,255,0.06)', border: '1px solid rgba(88,166,255,0.15)', borderRadius: 8 }}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10, color: 'var(--accent-blue)' }}>
              Setup Instructions
            </div>
            {[
              'Click "Copy Script" above',
              'Go to Google Ads → click Tools (wrench icon) → Bulk Actions → Scripts',
              'Click the blue "+" button to create a new script',
              'Delete any existing code and paste the copied script',
              'Click "Authorize" when prompted (allows the script to access your campaigns)',
              'Click "Preview" to test — check the Logs tab for results',
              `Click "Run frequency" → set to "${SYNC_OPTIONS.find(o => o.value === syncInterval)?.label}"`,
              'Click "Save" — the script will now sync automatically!',
            ].map((step, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 6, fontSize: 12, color: 'var(--text-secondary)' }}>
                <span style={{ fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', color: 'var(--accent-blue)', fontWeight: 600, flexShrink: 0 }}>{i + 1}.</span>
                <span>{step}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
